import * as React from "react";
import { useAppDispatch } from "../../redux/hooks";
import { authenticate } from "../../redux/reducers/user-slice";
import { userApi } from "../../api";
import { NavLink, useNavigate, useSearchParams } from "react-router";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SubmitHandler, useForm } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import ResetPasswordDialog from "./ResetPasswordDialog";
import HCaptcha from "@hcaptcha/react-hcaptcha";

enum SignInErrors {
  NO_ACCOUNT_FOUND = "NO_ACCOUNT_FOUND",
  INCORRECT_PASSWORD = "INCORRECT_PASSWORD",
}

enum ForgotPasswordErrors {
  CAPTCHA_REQUIRED = "CAPTCHA_REQUIRED",
}

type Inputs = {
  email: string;
  password: string;
};

export default ({ onRegisterClick }: { onRegisterClick?: VoidFunction }) => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [showPassResetDialog, setShowPassResetDialog] =
    React.useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = React.useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = React.useState<string>("");
  const hcaptchaRef = React.createRef<HCaptcha>();

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<Inputs>();

  React.useEffect(() => {
    if (searchParams.get("email")) {
      setValue("email", searchParams.get("email")!);
      setFocus("password");
    } else {
      setFocus("email");
    }
  }, [searchParams, setValue, setFocus]);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    setSubmitting(true);
    userApi
      .signIn(data.email, data.password)
      .then(() => {
        dispatch(authenticate()).then(() => {
          nav("/");
        });
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          if (err.response?.data.error === SignInErrors.INCORRECT_PASSWORD) {
            setError("password", { message: "Incorrect password." });
          } else if (
            err.response?.data.error === SignInErrors.NO_ACCOUNT_FOUND
          ) {
            setError("email", { message: "No account found." });
          } else {
            setError("root", {
              message: "An unknown error occured. Please try again later.",
            });
          }
        } else {
          setError("root", {
            message: "An unknown error occured. Please try again later.",
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onForgotClick = () => {
    const email = getValues().email;

    if (!/.+@.+\..+/.test(email)) {
      setError("email", {
        message: "An email is required to send password reset instructions.",
      });
      return;
    }

    userApi
      .sendPasswordResetEmail(email, captchaToken)
      .then(() => {
        setShowPassResetDialog(true);
        setError("email", { message: "" });
        setError("root", { message: "" });
        if (captchaToken) {
          hcaptchaRef.current?.resetCaptcha();
          setCaptchaToken("");
        }
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          const error = err.response?.data?.error;
          if (error === SignInErrors.NO_ACCOUNT_FOUND) {
            setError("email", { message: "No account found." });
          } else if (error === ForgotPasswordErrors.CAPTCHA_REQUIRED) {
            setShowCaptcha(true);
            setError("root", {
              message:
                "Please complete the captcha before requesting for another password reset email.",
            });
          } else {
            setError("root", {
              message: "An unknown error occured. Please try again later.",
            });
          }
        } else {
          setError("root", {
            message: "An unknown error occured. Please try again later.",
          });
        }
      });
  };

  const _onRegisterClick = () => {
    if (onRegisterClick) {
      onRegisterClick();
    } else {
      nav("/sign-up");
    }
  };

  return (
    <div className="w-80">
      <div className="text-[#333] text-center font-semibold text-2xl mb-6">
        Log in to Invited
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div>
            <Label htmlFor="email" className="text-xs">
              EMAIL OR USERNAME
            </Label>
            <Input
              type="text"
              id="email"
              className="rounded-xs"
              {...register("email", {
                required: "Email or username is required.",
              })}
            />
            {errors.email && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="mt-4">
            <Label htmlFor="password" className="text-xs">
              PASSWORD
            </Label>
            <Input
              type="password"
              id="password"
              className="rounded-xs"
              {...register("password", { required: "Password is required." })}
            />
            {errors.password && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.password?.message}
              </p>
            )}
            <span
              className="text-sm text-[#333] hover:underline cursor-pointer"
              onClick={onForgotClick}
            >
              Forgot your password?
            </span>
          </div>
          {showCaptcha && (
            <div className="mt-4">
              <HCaptcha
                sitekey={process.env.HCAPTCHA_SITE_KEY!}
                onVerify={setCaptchaToken}
                ref={hcaptchaRef}
              />
            </div>
          )}
          <Button
            type="submit"
            className="mt-4 w-full rounded-sm"
            disabled={submitting}
          >
            {submitting ? <Loader2 className="animate-spin" /> : "Log In"}
          </Button>
          {errors.root && (
            <p className="text-[#e30000] text-xs mt-1">{errors.root.message}</p>
          )}
          <div className="text-sm text-[#333] mt-2">
            <span>Need an account? </span>
            <span
              className="hover:underline cursor-pointer"
              onClick={_onRegisterClick}
            >
              Register
            </span>
          </div>
        </div>
      </form>
      <ResetPasswordDialog
        open={showPassResetDialog}
        close={() => setShowPassResetDialog(false)}
      />
    </div>
  );
};
